/**
 * ReportsPage
 *
 * Admin page for viewing reports and system health.
 */

import { useState } from 'react';
import { AdminLayout } from '../layout';
import {
  useReports,
  useSystemHealth,
  convertToCSV,
  downloadCSV,
} from './hooks';
import type { ReportType, DateRange } from './hooks';

const formatDate = (date: Date) => {
  return date.toISOString().split('T')[0];
};

const getStartOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
};

const getStartOfWeek = () => {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() - diff);
};

const PRESETS = [
  { label: 'This Week', start: getStartOfWeek(), end: new Date() },
  { label: 'This Month', start: getStartOfMonth(), end: new Date() },
  { label: 'Last 30 Days', start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), end: new Date() },
  { label: 'Last 90 Days', start: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000), end: new Date() },
];

export const ReportsPage = () => {
  const [reportType, setReportType] = useState<ReportType>('revenue');
  const [dateRange, setDateRange] = useState<DateRange>({
    start: getStartOfMonth(),
    end: new Date(),
  });
  const [activeTab, setActiveTab] = useState<'reports' | 'health'>('reports');

  const { report, isLoading: loadingReport } = useReports(reportType, dateRange);
  const { health, isLoading: loadingHealth } = useSystemHealth();

  const handleExport = () => {
    if (!report) return;
    const csv = convertToCSV(report);
    const dateStr = formatDate(new Date());
    downloadCSV(csv, `${reportType}-report-${dateStr}.csv`);
  };

  const getReportTitle = () => {
    switch (reportType) {
      case 'users':
        return 'User Registrations';
      case 'bookings':
        return 'Bookings';
      case 'revenue':
        return 'Platform Revenue';
      case 'payouts':
        return 'Payouts';
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-6xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Reports & System Health</h1>
            <p className="text-gray-500 mt-1">Monitor platform performance and generate reports</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-lg w-fit">
          <button
            onClick={() => setActiveTab('reports')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'reports'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Reports
          </button>
          <button
            onClick={() => setActiveTab('health')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'health'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            System Health
          </button>
        </div>

        {activeTab === 'reports' && (
          <>
            {/* Report Controls */}
            <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
              <div className="flex flex-wrap gap-4 items-end">
                {/* Report Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Report Type
                  </label>
                  <select
                    value={reportType}
                    onChange={(e) => setReportType(e.target.value as ReportType)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="revenue">Revenue</option>
                    <option value="bookings">Bookings</option>
                    <option value="users">User Registrations</option>
                    <option value="payouts">Payouts</option>
                  </select>
                </div>

                {/* Date Range Presets */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quick Select
                  </label>
                  <div className="flex gap-2">
                    {PRESETS.map((preset) => (
                      <button
                        key={preset.label}
                        onClick={() => setDateRange({ start: preset.start, end: preset.end })}
                        className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom Date Range */}
                <div className="flex gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
                    <input
                      type="date"
                      value={formatDate(dateRange.start)}
                      onChange={(e) =>
                        setDateRange({ ...dateRange, start: new Date(e.target.value) })
                      }
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                    <input
                      type="date"
                      value={formatDate(dateRange.end)}
                      onChange={(e) =>
                        setDateRange({ ...dateRange, end: new Date(e.target.value) })
                      }
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Export */}
                <button
                  onClick={handleExport}
                  disabled={loadingReport || !report}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  Export CSV
                </button>
              </div>
            </div>

            {/* Report Content */}
            {loadingReport ? (
              <div className="bg-white rounded-xl shadow-sm p-8">
                <div className="animate-pulse space-y-4">
                  <div className="h-8 bg-gray-200 rounded w-1/3" />
                  <div className="h-64 bg-gray-200 rounded" />
                  <div className="grid grid-cols-4 gap-4">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="h-20 bg-gray-200 rounded" />
                    ))}
                  </div>
                </div>
              </div>
            ) : report ? (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl shadow-sm p-4">
                    <p className="text-sm text-gray-500">Total</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {reportType === 'revenue' || reportType === 'payouts'
                        ? `$${report.summary.total.toLocaleString()}`
                        : report.summary.total.toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm p-4">
                    <p className="text-sm text-gray-500">Change</p>
                    <p
                      className={`text-2xl font-bold ${
                        report.summary.change >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      {report.summary.change >= 0 ? '+' : ''}
                      {report.summary.changePercent.toFixed(1)}%
                    </p>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm p-4">
                    <p className="text-sm text-gray-500">Daily Average</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {reportType === 'revenue' || reportType === 'payouts'
                        ? `$${report.summary.average.toLocaleString()}`
                        : report.summary.average.toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm p-4">
                    <p className="text-sm text-gray-500">Period</p>
                    <p className="text-lg font-bold text-gray-900">
                      {report.dataPoints.length} days
                    </p>
                  </div>
                </div>

                {/* Chart Placeholder */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="font-semibold text-lg mb-4">{getReportTitle()} Over Time</h3>
                  <div className="h-64 flex items-end gap-1">
                    {report.dataPoints.slice(-30).map((dp, i) => {
                      const maxValue = Math.max(...report.dataPoints.map((d) => d.value));
                      const height = (dp.value / maxValue) * 100;
                      return (
                        <div
                          key={i}
                          className="flex-1 bg-primary-500 rounded-t hover:bg-primary-600 transition-colors"
                          style={{ height: `${height}%` }}
                          title={`${dp.date}: ${dp.value}`}
                        />
                      );
                    })}
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-2">
                    <span>{formatDate(dateRange.start)}</span>
                    <span>{formatDate(dateRange.end)}</span>
                  </div>
                </div>

                {/* Breakdown */}
                {report.breakdown && (
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="font-semibold text-lg mb-4">Breakdown</h3>
                    <div className="space-y-3">
                      {report.breakdown.map((item) => (
                        <div key={item.label}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-700">{item.label}</span>
                            <span className="font-medium">
                              {reportType === 'revenue' || reportType === 'payouts'
                                ? `$${item.value.toLocaleString()}`
                                : item.value.toLocaleString()}{' '}
                              ({item.percent}%)
                            </span>
                          </div>
                          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary-500 rounded-full"
                              style={{ width: `${item.percent}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm p-12 text-center text-gray-500">
                No data available for the selected period
              </div>
            )}
          </>
        )}

        {activeTab === 'health' && (
          <>
            {loadingHealth ? (
              <div className="space-y-6">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-white rounded-xl shadow-sm p-6 animate-pulse">
                    <div className="h-6 bg-gray-200 rounded w-1/4 mb-4" />
                    <div className="h-32 bg-gray-200 rounded" />
                  </div>
                ))}
              </div>
            ) : health ? (
              <div className="space-y-6">
                {/* Services Status */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="font-semibold text-lg mb-4">Firebase Services</h3>
                  <div className="grid grid-cols-5 gap-4">
                    {health.services.map((service) => (
                      <div
                        key={service.name}
                        className="border border-gray-200 rounded-lg p-4 text-center"
                      >
                        <div
                          className={`w-3 h-3 rounded-full mx-auto mb-2 ${
                            service.status === 'operational'
                              ? 'bg-green-500'
                              : service.status === 'degraded'
                              ? 'bg-amber-500'
                              : 'bg-red-500'
                          }`}
                        />
                        <p className="font-medium text-sm text-gray-900">{service.name}</p>
                        <p className="text-xs text-gray-500 capitalize">{service.status}</p>
                        {service.latency && (
                          <p className="text-xs text-gray-400 mt-1">{service.latency}ms</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Storage */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="font-semibold text-lg mb-4">Storage Usage</h3>
                  <div className="flex items-center gap-6">
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary-500 rounded-full"
                          style={{ width: `${health.storage.percent}%` }}
                        />
                      </div>
                      <p className="text-sm text-gray-500 mt-2">
                        {health.storage.used} GB of {health.storage.total} GB used
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-gray-900">
                        {health.storage.percent}%
                      </p>
                      <p className="text-sm text-gray-500">used</p>
                    </div>
                  </div>
                </div>

                {/* Document Counts */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="font-semibold text-lg mb-4">Database Documents</h3>
                  <div className="grid grid-cols-7 gap-4">
                    {health.documents.map((doc) => (
                      <div
                        key={doc.collection}
                        className="bg-gray-50 rounded-lg p-3 text-center"
                      >
                        <p className="text-2xl font-bold text-gray-900">
                          {doc.count.toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-500 capitalize">{doc.collection}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recent Errors */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="font-semibold text-lg mb-4">Recent Errors</h3>
                  {health.recentErrors.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <svg
                        className="w-12 h-12 mx-auto mb-3 text-green-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <p>No recent errors</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {health.recentErrors.map((error) => (
                        <div
                          key={error.id}
                          className="flex items-center justify-between p-3 bg-red-50 border border-red-100 rounded-lg"
                        >
                          <div>
                            <p className="font-medium text-red-800">{error.message}</p>
                            <p className="text-sm text-red-600">
                              Last occurred:{' '}
                              {new Intl.DateTimeFormat('en-US', {
                                hour: '2-digit',
                                minute: '2-digit',
                              }).format(error.lastOccurred)}
                            </p>
                          </div>
                          <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                            {error.count}x
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default ReportsPage;
